import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Card, CircularProgress, Collapse, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { renderLiveReportHeatmap } from '../api/liveReports';

const formatAverage = (value) => Number.isFinite(value) ? Math.round(value).toLocaleString('en-IN') : '—';
const formatDate = (value, timezone) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: timezone, day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  }).format(date);
};

export default function LiveReportResults({ report }) {
  const [expanded, setExpanded] = useState(false);
  const [referenceError, setReferenceError] = useState(false);
  const [imageRatio, setImageRatio] = useState(null);
  const [rendering, setRendering] = useState(null);
  const [loadingHeatmap, setLoadingHeatmap] = useState(false);
  const [heatmapError, setHeatmapError] = useState('');
  const [retry, setRetry] = useState(0);
  const timezone = report.scope?.timezone || 'Asia/Kolkata';
  const baseUrl = (process.env.REACT_APP_STREAM_URL || '').trim().replace(/\/+$/, '');
  const cameraId = report.camera?.deviceid ?? report.camera?.live_id ?? report.camera?.id;
  const suppliedReference = report.reference_image
    || (report.reference && /^(https?:\/\/|\/)/i.test(report.reference) ? report.reference : '');
  const referenceUrl = report.reference_info?.available === false ? '' : suppliedReference
    ? new URL(suppliedReference, `${baseUrl}/`).href
    : baseUrl && cameraId != null && report.view_id != null
      ? `${baseUrl}/api/cameras/${encodeURIComponent(cameraId)}/image?view_id=${encodeURIComponent(report.view_id)}` : '';

  useEffect(() => {
    setReferenceError(false);
    setImageRatio(null);
  }, [referenceUrl]);

  useEffect(() => {
    if (!expanded) return undefined;
    let active = true;
    const controller = new AbortController();
    setLoadingHeatmap(true);
    setHeatmapError('');
    setRendering(null);
    renderLiveReportHeatmap(report, { signal: controller.signal }).then((result) => {
      if (active) setRendering(result);
    }).catch(() => {
      if (active) setHeatmapError('Unable to render the accumulated heatmap. Please retry.');
    }).finally(() => {
      if (active) setLoadingHeatmap(false);
    });
    return () => { active = false; controller.abort(); };
  }, [report, expanded, retry]);

  const zones = report.zones || [];
  const coverage = report.summary?.coverage_percent;
  const ratio = imageRatio || (rendering?.width && rendering?.height
    ? rendering.width / rendering.height : 16 / 9);
  const palette = rendering?.palette || [];
  const saturatedPixels = rendering?.saturated_pixels || 0;

  return (
    <Card variant="outlined" sx={{ mt: 3, p: { xs: 2, md: 3 }, boxShadow: 'none', borderColor: 'divider' }}>
      <Typography variant="h6">{report.camera?.name || 'Camera'} · Zone averages</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
        {formatDate(report.scope?.start, timezone)} → {formatDate(report.scope?.end, timezone)}
      </Typography>
      {!zones.length ? <Typography color="text.secondary">No zone observations found for the selected interval.</Typography> : (
        <TableContainer>
          <Table size="small" aria-label="Zone averages" sx={{ '& th': { bgcolor: 'background.neutral' }, '& td, & th': { borderColor: 'divider' } }}>
            <TableHead><TableRow>
              <TableCell>Zone</TableCell>
              <TableCell>Avg people present in zone (people)</TableCell>
              <TableCell>Avg time spent by people in zone (seconds)</TableCell>
            </TableRow></TableHead>
            <TableBody>{zones.map((zone) => <TableRow key={zone.id}>
              <TableCell>{zone.name}</TableCell>
              <TableCell>{formatAverage(zone.average_people_count)}</TableCell>
              <TableCell>{formatAverage(zone.average_observed_dwell_seconds)}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
      )}
      <Button color="inherit" onClick={() => setExpanded((current) => !current)} aria-expanded={expanded}
        aria-controls="accumulated-report-heatmap" sx={{ mt: 2, px: 0 }}>
        {expanded ? '▾' : '▸'}&nbsp; Show accumulated heatmap
      </Button>
      <Collapse in={expanded} id="accumulated-report-heatmap">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accumulated activity for the selected interval and zones, measured in person-seconds. This is historical activity, not live occupancy.
        </Typography>
        {loadingHeatmap && <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ mb: 2 }}>
          <CircularProgress size={20} /><Typography variant="body2">Rendering accumulated heatmap...</Typography>
        </Stack>}
        {heatmapError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setRetry((current) => current + 1)}>Retry</Button>}>{heatmapError}</Alert>}
        {rendering && !rendering.available && <Alert severity="info">No heatmap data available for this interval.</Alert>}
        {rendering?.available && <>
        {(!referenceUrl || referenceError) && <Alert severity="info" sx={{ mb: 2 }}>Reference image unavailable. The heatmap and zone outlines are shown without a camera image.</Alert>}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: `${ratio}`, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
          {referenceUrl && !referenceError && <Box component="img" src={referenceUrl} alt="Camera reference for the selected report view"
            onError={() => setReferenceError(true)} onLoad={(event) => setImageRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />}
          <Box component="img" src={rendering.overlay_png} alt="Accumulated activity heatmap"
            onError={() => setHeatmapError('Unable to display the rendered heatmap. Please retry.')}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          <Box component="svg" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-label="Report zone outlines"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', color: 'warning.light' }}>
            {zones.filter((zone) => Array.isArray(zone.polygon) && zone.polygon.length).map((zone) => {
              const [labelX, labelY] = zone.polygon[0];
              return <g key={zone.id}>
                <polygon points={zone.polygon.map(([x, y]) => `${x * 1000},${y * 1000}`).join(' ')}
                  fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                <text x={Math.min(920, labelX * 1000 + 8)} y={Math.min(975, labelY * 1000 + 22)} fill="currentColor" stroke="#0B1220"
                  strokeWidth="3" paintOrder="stroke" fontSize="18">{zone.name}</text>
              </g>;
            })}
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, maxWidth: 340 }}>
          <Typography variant="caption">{rendering.scale_min}</Typography>
          <Box sx={{ flex: 1, height: 6, borderRadius: 1, background: palette.length ? `linear-gradient(to right, ${palette.join(', ')})` : 'transparent' }} />
          <Typography variant="caption">{rendering.scale_max}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>{rendering.units} · {rendering.scale_mode} linear scale</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Blue = low/zero baseline. Smoothing changes appearance, not recorded measurements.</Typography>
        {saturatedPixels > 0 && <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
          {saturatedPixels.toLocaleString('en-IN')} display pixels exceed the maximum of {rendering.scale_max} and appear red.
        </Typography>}
        </>}
      </Collapse>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Only available observations are included{Number.isFinite(coverage) ? ` (${coverage.toFixed(1)}% coverage)` : ''}. Missing footage is not counted as zero.
      </Typography>
    </Card>
  );
}

LiveReportResults.propTypes = { report: PropTypes.object.isRequired };
